/**
 * MarkeeMix Regional Toolkit — 4 high-leverage Indic AI tools in one page.
 * Route: /tools/regional-toolkit
 *
 * Tabs:
 *   1. Regional Ad Studio        — English brief → 5-language ad copy
 *   2. WhatsApp Broadcast         — Type once, get 8 Indic translations + voice notes
 *   3. Podcast → Blog             — Indic audio → English blog draft
 *   4. Call Analyzer              — Upload sales call → intent + objections + next steps
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Megaphone, MessageSquare, Headphones, PhoneCall,
  Loader2, Copy, Download, Upload, Play, Sparkles,
  Target, Users, ClipboardList,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api/indic`;

const ALL_LANGS = [
  { code: "hi-IN", label: "Hindi" },
  { code: "ta-IN", label: "Tamil" },
  { code: "te-IN", label: "Telugu" },
  { code: "bn-IN", label: "Bengali" },
  { code: "mr-IN", label: "Marathi" },
  { code: "gu-IN", label: "Gujarati" },
  { code: "kn-IN", label: "Kannada" },
  { code: "ml-IN", label: "Malayalam" },
  { code: "pa-IN", label: "Punjabi" },
  { code: "od-IN", label: "Odia" },
];

const INTENT_COLOR = {
  buy_now: "bg-emerald-100 text-emerald-800",
  evaluating: "bg-blue-100 text-blue-800",
  comparing: "bg-purple-100 text-purple-800",
  price_sensitive: "bg-amber-100 text-amber-800",
  needs_demo: "bg-indigo-100 text-indigo-800",
  needs_followup: "bg-cyan-100 text-cyan-800",
  not_interested: "bg-red-100 text-red-800",
};

const SENT_COLOR = { positive: "text-emerald-600", neutral: "text-neutral-600", negative: "text-red-600" };

const TABS = [
  { id: "ads", label: "Regional Ad Studio", Icon: Megaphone, desc: "Google/Meta ad copy in 5 Indic languages" },
  { id: "whatsapp", label: "WhatsApp Broadcast", Icon: MessageSquare, desc: "One message → 10 language translations + voice notes" },
  { id: "podcast", label: "Podcast → Blog", Icon: Headphones, desc: "Indic audio → SEO-ready English blog draft" },
  { id: "call", label: "Call Analyzer", Icon: PhoneCall, desc: "Sales call → intent + objections + next steps" },
];

function copy(t) { navigator.clipboard.writeText(t); toast.success("Copied!"); }

// ── 1. Regional Ad Studio ────────────────────────────────────────
function AdStudio() {
  const [form, setForm] = useState({ product: "", benefit: "", audience: "Indian consumers", cta: "Shop now", platform: "google" });
  const [langs, setLangs] = useState(["hi-IN", "ta-IN", "te-IN", "mr-IN", "bn-IN"]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (code) => setLangs(langs.includes(code) ? langs.filter(l => l !== code) : [...langs, code]);

  const run = async () => {
    if (!form.product.trim() || !form.benefit.trim()) { toast.error("Product and benefit are required"); return; }
    if (langs.length === 0) { toast.error("Select at least one language"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/ad-copy`, { ...form, languages: langs });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ad generation failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">
          Product / service *
          <Input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })}
            placeholder="e.g. Fresh Arabica coffee beans" className="mt-1" data-testid="ads-product" />
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Audience
          <Input value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
            placeholder="e.g. Home baristas" className="mt-1" data-testid="ads-audience" />
        </label>
      </div>
      <label className="text-xs font-semibold text-neutral-600">
        Core benefit *
        <Textarea value={form.benefit} onChange={(e) => setForm({ ...form, benefit: e.target.value })}
          rows={2} placeholder="e.g. Roasted the day it ships, café-quality at home, free next-day delivery"
          className="mt-1" data-testid="ads-benefit" />
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs font-semibold text-neutral-600">
          Preferred CTA
          <Input value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })}
            className="mt-1" data-testid="ads-cta" />
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Platform
          <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="ads-platform">
            <option value="google">Google Ads</option>
            <option value="meta">Meta (Facebook + Instagram)</option>
            <option value="both">Both</option>
          </select>
        </label>
      </div>
      <div>
        <div className="text-xs font-semibold text-neutral-600 mb-2">Target languages ({langs.length})</div>
        <div className="flex flex-wrap gap-2">
          {ALL_LANGS.map(l => {
            const on = langs.includes(l.code);
            return (
              <button key={l.code} type="button" onClick={() => toggle(l.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  on ? "bg-[#A855F7] text-white border-[#A855F7]" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                }`} data-testid={`ads-lang-${l.code}`}>
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
      <Button onClick={run} disabled={busy} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="ads-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Crafting regional ads…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate ads in {langs.length} languages</>}
      </Button>

      {result && (
        <div className="space-y-4" data-testid="ads-result">
          <Card className="p-5 border-neutral-200 bg-neutral-50">
            <div className="flex items-center justify-between mb-3">
              <Badge className="bg-neutral-200 text-neutral-800 border-transparent">English (source)</Badge>
              <Button size="sm" variant="outline" onClick={() => copy(JSON.stringify(result.english, null, 2))}>
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider">Headlines</p>
              {(result.english.headlines || []).map((h, i) => <p key={i} className="font-semibold text-[#111111]">{h}</p>)}
              <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider pt-2">Descriptions</p>
              {(result.english.descriptions || []).map((d, i) => <p key={i} className="text-neutral-700">{d}</p>)}
              <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider pt-2">CTA</p>
              <p className="font-semibold text-[#A855F7]">{result.english.cta}</p>
            </div>
          </Card>
          {(result.localised || []).map((loc, i) => (
            <Card key={i} className="p-5 border-neutral-200">
              <div className="flex items-center justify-between mb-3">
                <Badge className="bg-orange-100 text-orange-800 border-transparent">{loc.language_label}</Badge>
                <Button size="sm" variant="outline" onClick={() => copy([...(loc.headlines||[]),...(loc.descriptions||[]),loc.cta].join("\n"))}>
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                {(loc.headlines || []).map((h, j) => <p key={j} className="font-semibold text-[#111111]">{h}</p>)}
                <div className="border-t border-neutral-100 pt-2 space-y-1">
                  {(loc.descriptions || []).map((d, j) => <p key={j} className="text-neutral-700 text-xs">{d}</p>)}
                </div>
                <p className="font-semibold text-[#A855F7] text-xs pt-1">{loc.cta}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 2. WhatsApp Broadcast ────────────────────────────────────────
function WhatsAppComposer() {
  const [message, setMessage] = useState("");
  const [langs, setLangs] = useState(["hi-IN", "ta-IN", "te-IN", "mr-IN", "bn-IN"]);
  const [includeVoice, setIncludeVoice] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (code) => setLangs(langs.includes(code) ? langs.filter(l => l !== code) : [...langs, code]);

  const run = async () => {
    if (!message.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await axios.post(`${API}/whatsapp`, { message, languages: langs, include_voice: includeVoice });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Composer failed");
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <label className="text-xs font-semibold text-neutral-600 block">
        Your message (any language)
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
          rows={4} placeholder="Type your WhatsApp broadcast in any language. Example: 'Diwali special — 30% off all products this weekend only!'"
          className="mt-1" data-testid="wa-message" />
      </label>
      <div>
        <div className="text-xs font-semibold text-neutral-600 mb-2">Broadcast to ({langs.length} languages)</div>
        <div className="flex flex-wrap gap-2">
          {ALL_LANGS.map(l => {
            const on = langs.includes(l.code);
            return (
              <button key={l.code} type="button" onClick={() => toggle(l.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  on ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                }`} data-testid={`wa-lang-${l.code}`}>
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-neutral-700">
        <input type="checkbox" checked={includeVoice} onChange={(e) => setIncludeVoice(e.target.checked)}
          data-testid="wa-voice-toggle" />
        Also generate a voice note per language (adds ~15s)
      </label>
      <Button onClick={run} disabled={busy || !message.trim()} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="wa-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Translating & voicing…</> : <><MessageSquare className="w-4 h-4 mr-2" /> Compose broadcast</>}
      </Button>

      {result && (
        <div className="space-y-3" data-testid="wa-result">
          {(result.broadcasts || []).map((b, i) => (
            <Card key={i} className="p-4 border-neutral-200">
              <div className="flex items-center justify-between mb-2">
                <Badge className="bg-emerald-100 text-emerald-800 border-transparent">{b.language_label}</Badge>
                <Button size="sm" variant="outline" onClick={() => copy(b.translated_text)}>
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-sm text-[#111111] leading-relaxed whitespace-pre-wrap">{b.translated_text}</p>
              {b.voice_base64 && (
                <div className="mt-3">
                  <audio controls src={`data:audio/wav;base64,${b.voice_base64}`} className="w-full h-9" />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 3. Podcast → Blog ────────────────────────────────────────────
function PodcastToBlog() {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("auto");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!file) { toast.error("Pick a podcast file first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_language", language);
      const r = await axios.post(`${API}/podcast-to-blog`, fd, { timeout: 240000 });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Podcast processing failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-[1fr_180px] gap-3">
        <label className="block border-2 border-dashed border-neutral-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#A855F7]">
          <input type="file" accept=".mp3,.m4a,.wav,.mp4,.webm,.ogg" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="podcast-file" />
          <Upload className="w-6 h-6 mx-auto text-neutral-400 mb-1" />
          <div className="text-sm font-semibold text-neutral-700">{file ? file.name : "Upload podcast audio"}</div>
          <div className="text-xs text-neutral-500 mt-1">Any Indic language · up to 100 MB</div>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Source language
          <select value={language} onChange={(e) => setLanguage(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="podcast-lang">
            <option value="auto">Auto-detect</option>
            <option value="hi">Hindi</option>
            <option value="ta">Tamil</option>
            <option value="te">Telugu</option>
            <option value="bn">Bengali</option>
            <option value="mr">Marathi</option>
            <option value="gu">Gujarati</option>
            <option value="kn">Kannada</option>
            <option value="ml">Malayalam</option>
            <option value="pa">Punjabi</option>
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy || !file} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="podcast-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing & structuring…</> : <><Headphones className="w-4 h-4 mr-2" /> Turn into a blog draft</>}
      </Button>

      {result?.blog && (
        <div className="space-y-4" data-testid="podcast-result">
          <Card className="p-5 border-neutral-200">
            <Badge className="bg-emerald-100 text-emerald-800 border-transparent mb-3">
              {Math.round(result.duration || 0)}s · {result.detected_language}
            </Badge>
            <h2 className="font-heading text-2xl font-semibold text-[#111111] mb-2">{result.blog.title}</h2>
            {result.blog.tldr && <p className="text-sm text-neutral-600 italic bg-neutral-50 border-l-4 border-[#A855F7] p-3 my-3">TL;DR: {result.blog.tldr}</p>}
            {(result.blog.sections || []).map((s, i) => (
              <div key={i} className="mt-4">
                <h3 className="font-heading text-lg font-semibold text-[#111111]">{s.heading}</h3>
                <p className="text-sm text-neutral-700 leading-relaxed mt-1">{s.body}</p>
              </div>
            ))}
            {(result.blog.quotes || []).length > 0 && (
              <div className="mt-6 space-y-2">
                {result.blog.quotes.map((q, i) => (
                  <blockquote key={i} className="border-l-4 border-orange-400 pl-4 py-1 text-sm text-neutral-700 italic">
                    &ldquo;{q}&rdquo;
                  </blockquote>
                ))}
              </div>
            )}
            {result.blog.cta && <p className="mt-6 font-semibold text-[#A855F7]">{result.blog.cta}</p>}
            <div className="flex gap-2 mt-6">
              <Button size="sm" variant="outline" onClick={() => copy(result.transcript)}>
                <ClipboardList className="w-3 h-3 mr-1" /> Copy transcript
              </Button>
              <Button size="sm" variant="outline" onClick={() => copy(JSON.stringify(result.blog, null, 2))}>
                <Copy className="w-3 h-3 mr-1" /> Copy blog JSON
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── 4. Call Analyzer ─────────────────────────────────────────────
function CallAnalyzer() {
  const [file, setFile] = useState(null);
  const [language, setLanguage] = useState("auto");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("language", language);
      fd.append("mode", "translate");
      const r = await axios.post(`${API}/call-analyze`, fd, { timeout: 180000 });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Call analysis failed");
    } finally { setBusy(false); }
  };

  const a = result?.analysis;

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-[1fr_180px] gap-3">
        <label className="block border-2 border-dashed border-neutral-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#A855F7]">
          <input type="file" accept=".mp3,.m4a,.wav,.mp4,.webm,.ogg" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="call-file" />
          <Upload className="w-6 h-6 mx-auto text-neutral-400 mb-1" />
          <div className="text-sm font-semibold text-neutral-700">{file ? file.name : "Upload call recording"}</div>
          <div className="text-xs text-neutral-500 mt-1">MP3 · WAV · M4A · MP4</div>
        </label>
        <label className="text-xs font-semibold text-neutral-600">
          Call language
          <select value={language} onChange={(e) => setLanguage(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-neutral-300 px-3 text-sm" data-testid="call-lang">
            <option value="auto">Auto-detect</option>
            <option value="hi">Hindi</option>
            <option value="en">English</option>
            <option value="ta">Tamil</option>
            <option value="te">Telugu</option>
            <option value="bn">Bengali</option>
            <option value="mr">Marathi</option>
          </select>
        </label>
      </div>
      <Button onClick={run} disabled={busy || !file} className="w-full h-11 bg-[#111111] hover:bg-[#333] text-white" data-testid="call-run">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing call…</> : <><PhoneCall className="w-4 h-4 mr-2" /> Analyse call</>}
      </Button>

      {a && (
        <div className="space-y-4" data-testid="call-result">
          <div className="grid sm:grid-cols-3 gap-3">
            <Card className="p-4 border-neutral-200">
              <p className="text-[10px] uppercase font-bold text-neutral-500 tracking-wider">Intent</p>
              <p className={`mt-1 inline-block px-2 py-1 rounded text-xs font-bold ${INTENT_COLOR[a.intent] || "bg-neutral-100 text-neutral-800"}`}>{a.intent}</p>
            </Card>
            <Card className="p-4 border-neutral-200">
              <p className="text-[10px] uppercase font-bold text-neutral-500 tracking-wider">Sentiment</p>
              <p className={`mt-1 font-heading text-xl font-semibold capitalize ${SENT_COLOR[a.sentiment] || ""}`}>{a.sentiment}</p>
            </Card>
            <Card className="p-4 border-neutral-200">
              <p className="text-[10px] uppercase font-bold text-neutral-500 tracking-wider">Deal confidence</p>
              <p className="mt-1 font-heading text-2xl font-semibold text-[#111111]">{a.deal_confidence}<span className="text-sm text-neutral-400">/100</span></p>
            </Card>
          </div>
          <Card className="p-5 border-neutral-200">
            <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider mb-2 flex items-center gap-1">
              <Target className="w-3 h-3" /> Summary
            </p>
            <p className="text-sm text-neutral-800 leading-relaxed">{a.summary}</p>
          </Card>
          {a.customer_pain_points?.length > 0 && (
            <Card className="p-5 border-neutral-200">
              <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider mb-2">Customer pain points</p>
              <ul className="text-sm space-y-1 list-disc list-inside">
                {a.customer_pain_points.map((p, i) => <li key={i} className="text-neutral-700">{p}</li>)}
              </ul>
            </Card>
          )}
          {a.objections?.length > 0 && (
            <Card className="p-5 border-neutral-200">
              <p className="text-xs uppercase text-neutral-500 font-bold tracking-wider mb-3">Objections + suggested responses</p>
              <div className="space-y-3">
                {a.objections.map((o, i) => (
                  <div key={i} className="border-l-4 border-red-400 pl-3">
                    <p className="text-sm font-semibold text-[#111111]">❌ {o.objection}</p>
                    <p className="text-sm text-emerald-700 mt-1">✓ {o.suggested_response}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {a.buying_signals?.length > 0 && (
            <Card className="p-5 border-neutral-200 bg-emerald-50">
              <p className="text-xs uppercase text-emerald-700 font-bold tracking-wider mb-2">Buying signals</p>
              <ul className="text-sm space-y-1 list-disc list-inside">
                {a.buying_signals.map((b, i) => <li key={i} className="text-emerald-900">{b}</li>)}
              </ul>
            </Card>
          )}
          {a.next_steps?.length > 0 && (
            <Card className="p-5 border-neutral-200 bg-purple-50">
              <p className="text-xs uppercase text-purple-700 font-bold tracking-wider mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Next steps for you
              </p>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                {a.next_steps.map((s, i) => <li key={i} className="text-purple-900 font-medium">{s}</li>)}
              </ol>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default function RegionalToolkitPage() {
  const [tab, setTab] = useState("ads");
  const Active = { ads: AdStudio, whatsapp: WhatsAppComposer, podcast: PodcastToBlog, call: CallAnalyzer }[tab];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <PremiumNav />

      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-orange-100 text-orange-800 border-transparent">🇮🇳 Made for India</Badge>
            <Badge className="bg-[#A855F7]/10 text-[#7C3AED] border-transparent">MarkeeMix Regional Toolkit</Badge>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#111111] tracking-tight">
            Regional Toolkit
          </h1>
          <p className="text-neutral-600 mt-3 max-w-2xl text-base">
            Four production-ready workflows built on top of the MarkeeMix Indic AI stack —
            multi-language ads, WhatsApp broadcasts, podcast repurposing, and sales-call analysis.
          </p>
        </div>
      </section>

      <div className="border-b border-neutral-200 bg-white sticky top-16 z-10">
        <div className="max-w-6xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-4 border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                  active ? "border-[#A855F7] text-[#7C3AED]" : "border-transparent text-neutral-500 hover:text-neutral-800"
                }`} data-testid={`toolkit-tab-${t.id}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <div className="mb-4 text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> {TABS.find(t => t.id === tab)?.desc}
        </div>
        <Card className="p-6 border-neutral-200 bg-white">
          <Active />
        </Card>

        <div className="mt-8 text-center text-xs text-neutral-500">
          Part of the <span className="text-[#7C3AED] font-semibold">MarkeeMix Indic AI</span> stack
        </div>
      </main>

      <PremiumFooter />
    </div>
  );
}
